import LoginModal from "components/LoginModal/LoginModal";
import { AuthContext } from "context/appContext";
import { motion } from "framer-motion";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { ArrowRight, Award, CircleCheck, CircleSlash, Loader2 } from "lucide-react";
import { useContext, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { getAllPlans, paySubscription } from "redux/features/subscription/subscriptionSlice";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { EASING } from "../../utils/easingUtils";

// Define interfaces
interface PlanFeature {
  id: number;
  description: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  PlanFeatures: {
    value: string;
    planId: number;
    featureId: number;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  };
}

interface Plan {
  id: number;
  name: string;
  amount: string;
  currency: string;
  interval: string;
  priceId: string;
  productId: string;
  trial_periode: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  Features: PlanFeature[];
}

interface PlanState {
  [key: string]: string | null;
}

const SubscriptionPlans = () => {
  const dispatch = useAppDispatch();
  const plansList = useAppSelector((state) => state.subscription.allPlans) as { plans: Plan[] };
  const [planData, setPlanData] = useState<PlanState>({});
  const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});
  const [isLoading, setIsLoading] = useState(true);
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const ref = useRef(null);

  // Fetch plans on mount
  useEffect(() => {
    const fetchPlans = async () => {
      setIsLoading(true);
      try {
        await dispatch(getAllPlans()).unwrap();
      } catch (err) {
        // Handle error with toast notification already provided
        toast.error("Erreur lors du chargement des plans");
      } finally {
        setIsLoading(false);
      }
    };
    fetchPlans();
  }, [dispatch]);

  // Update plan data when plansList changes
  useEffect(() => {
    if (plansList?.plans && Array.isArray(plansList.plans) && plansList.plans.length > 0) {
      const newPlanData: PlanState = {};
      plansList.plans.forEach((plan) => {
        newPlanData[`${plan.name.toLowerCase()}Price`] = plan.amount;
        newPlanData[`${plan.name.toLowerCase()}PriceId`] = plan.priceId;
      });
      setPlanData(newPlanData);
    }
  }, [plansList]);
  // Handle subscription
  const handleSubscribe = async (plan: {
    name: string;
    priceId: string;
    interval: string;
    trial_periode: number;
  }) => {
    const { name, priceId, interval, trial_periode } = plan;
    const isAuthenticated = authContext?.isAuthenticated;
    const isFreePlan = name === "Gratuit";
    const planKey = `${name}-${interval}`;

    if (!isAuthenticated && !isFreePlan) {
      setShowLoginModal(true);
      return;
    }

    if (isFreePlan) {
      navigate(isAuthenticated ? "/add-location" : "/signup/client");
      return;
    }

    setLoadingStates((prev) => ({ ...prev, [planKey]: true }));

    try {
      const sessionUrl = await dispatch(paySubscription({ name, priceId, trial_periode })).unwrap();
      window.location.href = sessionUrl;
    } catch (err: any) {
      if (err?.sessionUrl) {
        window.location.href = err.sessionUrl;
      } else {
        // Handle subscription error appropriately
        //alert(err?.message || "Erreur inconnue.");
        window.location.href = err?.message;
      }
    } finally {
      setLoadingStates((prev) => ({ ...prev, [planKey]: false }));
    }
  };

  const featureLabels: { id: number; description: string }[] =
    plansList?.plans && Array.isArray(plansList.plans)
      ? Array.from(
          new Map(
            plansList.plans.flatMap((plan) =>
              plan.Features.map((feature) => [feature.id, { id: feature.id, description: feature.description }])
            )
          ).values()
        )
      : [];

  const getFeatureValue = (value: string) => {
    if (value.toLocaleLowerCase() === "oui") {
      return (
        <span className="flex items-center gap-2">
          <CircleCheck className="w-5 h-5 text-green-500" />
        </span>
      );
    } else if (value.toLocaleLowerCase() === "non") {
      return <CircleSlash className="w-5 h-5 text-red-500" />;
    } else {
      return <span>{value}</span>;
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: EASING.easeOut },
    },
  };

  const buttonVariants = {
    hover: { scale: 1.05, boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)" },
    tap: { scale: 0.95 },
  };

  const intervalLabel: { [key: string]: string } = {
    day: "jour",
    week: "semaine",
    month: "mois",
    year: "an",
  };

  return (
    <div className="py-20 dark:from-gray-800 dark:to-gray-800">
      <div className="container px-6 mx-auto max-w-7xl">
        <motion.div className="mb-16 text-center">
          <h2 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white md:text-3xl">
            Nos options d’abonnements
          </h2>
          <p className="max-w-2xl mx-auto mt-4 mb-6 text-sm text-center text-gray-600 dark:text-white">
            Profitez d’un accès personnalisé à nos services grâce à nos offres d’abonnement conçues pour répondre à
            vos besoins.
          </p>
        </motion.div>

        {isLoading ? (
          <div className="flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-eventToBee" />
            <span className="ml-2 text-lg text-gray-600 dark:text-gray-300">Chargement des plans...</span>
          </div>
        ) : !plansList?.plans || !Array.isArray(plansList.plans) || plansList.plans.length === 0 ? (
          <div className="text-lg text-center text-gray-600 dark:text-gray-300">
            Aucun plan disponible pour le moment.
          </div>
        ) : (
          <motion.div
            ref={ref}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 gap-8 md:grid-cols-3"
          >
            {plansList.plans.map((plan) => (
              <motion.div
                key={`${plan.name}-${plan.interval}`}
                variants={cardVariants}
                className="relative flex flex-col w-full max-w-lg p-8 bg-white shadow-xl dark:bg-gray-500 rounded-2xl"
              >
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-eventToBee dark:text-blue-300">
                    {plan.name}{" "}
                    <span className="text-base text-gray-600 dark:text-gray-400">
                      {plan.name === "Gratuit"
                        ? ""
                        : plan.trial_periode === 0
                          ? ""
                          : ` (${plan.trial_periode} jours offerts)`}
                    </span>
                  </h3>
                  <div className="mt-4">
                    <span className="text-4xl font-extrabold text-gray-900 dark:text-white">{plan.amount} €</span>
                    <span className="ml-2 text-lg text-gray-600 dark:text-gray-400">
                      TTC /{intervalLabel[plan.interval]}
                    </span>
                  </div>
                </div>
                <ul className="flex-1 mt-8 space-y-4 text-lg leading-relaxed">
                  {featureLabels.map((feature) => {
                    const featureData = plan.Features.find((f) => f.id === feature.id);
                    return (
                      <li key={feature.id} className="flex items-start gap-4 text-gray-700 dark:text-gray-300">
                        <Award className="flex-shrink-0 w-5 h-5 mt-1 text-eventToBee" />
                        <div className="flex-1">
                          <span className="block font-medium">{feature.description}</span>
                          <span className="block text-sm text-gray-600 dark:text-gray-400">
                            {featureData ? getFeatureValue(featureData.PlanFeatures.value) : "-"}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                  <div className="flex items-center ">
                    <div className="relative ml-2 group">
                      <svg
                        className="w-5 h-5 text-neutral-500 dark:text-neutral-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 16h-1v-4h-1m1-4h.01M12 3a9 9 0 100 18 9 9 0 000-18z"
                        ></path>
                      </svg>
                    </div>

                    <div>
                      <span className="px-2 py-1 mb-2 text-sm text-neutral-500 whitespace-nowrap">
                        Tarif incluant la TVA
                      </span>
                    </div>
                  </div>
                </ul>
                <motion.button
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  className="flex items-center justify-center gap-2 px-6 py-4 mt-8 text-lg font-semibold text-white rounded-full bg-eventToBee hover:from-blue-600 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handleSubscribe(plan)}
                  disabled={loadingStates[`${plan.name}-${plan.interval}`]}
                >
                  {loadingStates[`${plan.name}-${plan.interval}`] ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      {plan.name === "Gratuit" && authContext?.isAuthenticated
                        ? "Ajouter espace"
                        : plan.name === "Gratuit"
                          ? "Inscrivez-vous"
                          : "S'abonner maintenant"}{" "}
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </motion.button>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
      {showLoginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black opacity-50"></div>
          <div className="flex flex-col items-center p-6 bg-white rounded-lg dark:bg-neutral-900">
            <div className="self-end mb-4">
              <ButtonSecondary onClick={() => setShowLoginModal(false)}>X</ButtonSecondary>
            </div>
            <LoginModal LoginModal={(e: boolean) => setShowLoginModal(e)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlans;
